import React from 'react';
import { cn } from '@/lib/utils';
import type { FunctionDefinition } from '@glueapp/graph-types';
import type { AgentInstance } from './agentTypes';

interface Props {
    agent: AgentInstance;
    functions: FunctionDefinition[];
    navigateToFunction: (functionId: string) => void;
}

/**
 * Small clickable badge rendered at agent's root position when the agent
 * is working inside a function that differs from the current view.
 */
export const AgentFunctionBadge: React.FC<Props> = ({ agent, functions, navigateToFunction }) => {
    const functionName = agent.functionId ? (functions.find(func => func.id === agent.functionId)?.name ?? 'Function') : 'Root';

    return (
        <div
            style={{
                position: 'absolute',
                left: agent.position.x,
                top: agent.position.y,
                pointerEvents: 'auto',
                zIndex: 90,
            }}
            onClick={event => {
                event.stopPropagation();
                if (agent.functionId) {
                    navigateToFunction(agent.functionId);
                }
            }}
            onPointerDown={event => event.stopPropagation()}
            className={cn(
                'flex items-center gap-1.5 px-2.5 py-1.5 rounded-full cursor-pointer',
                'bg-white/90 dark:bg-neutral-900/90 backdrop-blur-sm',
                'border border-white/30 dark:border-neutral-700/50',
                'shadow-sm hover:shadow-md transition-shadow',
                'select-none',
            )}
        >
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: agent.color.light }} />
            <span className="text-[10px] font-medium text-neutral-600 dark:text-neutral-300 whitespace-nowrap">{functionName}</span>
            {(agent.agentStatus === 'thinking' || agent.agentStatus === 'tool_calling') && (
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: agent.color.light }} />
            )}
        </div>
    );
};
